import ResizableContainer from "./ResizableContainer";

## Basic Usage

A single-edge resizable container. Drag the visible edge (here the left side)
to grow or shrink the panel along the resize axis. Defaults give a 280-800px
range starting at 400px.

<div style={{ display: "flex", height: "240px", border: "1px dashed #cbd5e1", background: "#f8fafc" }}>
  <div style={{ flex: 1, padding: "12px", color: "#475569" }}>Sibling pane (flex: 1)</div>
  <ResizableContainer>
    <div style={{ height: "100%", padding: "12px", background: "white", border: "1px solid #e2e8f0" }}>
      Drag the left edge to resize me.
    </div>
  </ResizableContainer>
</div>

---

## Sides

The `side` prop picks which edge carries the drag handle. `left` and `right`
resize horizontally; `top` and `bottom` resize vertically.

<div style={{ display: "flex", gap: "1rem" }}>
  <div style={{ flex: 1, height: "200px", display: "flex" }}>
    <ResizableContainer side="left" defaultSize={180} minSize={120} maxSize={300}>
      <div style={{ height: "100%", padding: "8px", background: "#eff6ff", border: "1px solid #bfdbfe" }}>
        side="left" — drag left edge
      </div>
    </ResizableContainer>
    <div style={{ flex: 1, padding: "8px", color: "#64748b" }}>main</div>
  </div>
  <div style={{ flex: 1, height: "200px", display: "flex" }}>
    <div style={{ flex: 1, padding: "8px", color: "#64748b" }}>main</div>
    <ResizableContainer side="right" defaultSize={180} minSize={120} maxSize={300}>
      <div style={{ height: "100%", padding: "8px", background: "#eff6ff", border: "1px solid #bfdbfe" }}>
        side="right" — drag right edge
      </div>
    </ResizableContainer>
  </div>
</div>

---

## Persistent Size

`storageKey` persists the user's last-chosen size in `localStorage`. Use a
namespaced key per surface (e.g. `studio:agent-panel-width`).

<div style={{ display: "flex", height: "180px" }}>
  <div style={{ flex: 1, padding: "8px", color: "#64748b" }}>main</div>
  <ResizableContainer storageKey="ui-library:resizable-demo" defaultSize={320}>
    <div style={{ height: "100%", padding: "8px", background: "#ecfdf5", border: "1px solid #a7f3d0" }}>
      Resize me, then reload — my width sticks (key:
      <code style={{ fontFamily: "monospace" }}>ui-library:resizable-demo</code>).
    </div>
  </ResizableContainer>
</div>

---

## Collapsible

When `collapsible` is set and the user drags past `minSize` by
`collapseThreshold` (default 60px), `onCollapse` fires. Typical consumer
behaviour: flip a Redux flag that unmounts this container from the parent
layout. Hysteresis prevents accidental collapse when a user just hits the
minimum.

export function CollapsibleExample() {
  const [open, setOpen] = React.useState(true);
  if (!open) {
    return (
      <div style={{ height: "180px", display: "flex", alignItems: "center", padding: "12px" }}>
        <button type="button" onClick={() => setOpen(true)}>
          Re-open panel
        </button>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", height: "180px" }}>
      <div style={{ flex: 1, padding: "8px", color: "#64748b" }}>main</div>
      <ResizableContainer
        defaultSize={280}
        minSize={200}
        maxSize={420}
        collapsible
        onCollapse={() => setOpen(false)}
      >
        <div style={{ height: "100%", padding: "8px", background: "#fff7ed", border: "1px solid #fed7aa" }}>
          Drag past minSize − 60px to collapse.
        </div>
      </ResizableContainer>
    </div>
  );
}

<CollapsibleExample />

---

## Keyboard Resize

The drag handle is focusable (`tabIndex=0`, `role="separator"`). Once
focused, arrow keys nudge by 16px per press, `Home` snaps to `minSize`,
`End` snaps to `maxSize`. Arrow direction follows the docking — for a
`side="left"` panel, ArrowLeft grows.

<div style={{ display: "flex", height: "180px" }}>
  <div style={{ flex: 1, padding: "8px", color: "#64748b" }}>Focus the right edge, then press ←/→.</div>
  <ResizableContainer>
    <div style={{ height: "100%", padding: "8px", background: "#faf5ff", border: "1px solid #e9d5ff" }}>
      tab here, then resize with the keyboard.
    </div>
  </ResizableContainer>
</div>
